// Two-mode banner for the map:
//
// (a) FOCUS MODE — when the URL contains `?focus=…&region=…`. The user
//     just arrived from /it/recommendations and we need to make it
//     obvious that a specific set of comuni is highlighted on the map
//     (otherwise they're invisible amongst 8 000 dots). Banner is
//     always-visible (no dismiss), terra-600 prominent strip below the
//     header, with a "Clear" action that strips the focus params and
//     resets filters.
//
// (b) QUIZ MODE — when the URL hash carries `fq=1` (legacy from quiz
//     result → map navigation). Renders the original dismissable pill
//     near the top of the map. One-time read so useUrlSync rewriting
//     the hash later doesn't kill the pill on first interaction.
//
// Source priority for the persona teaser (quiz mode):
//   1. `q=` blob in the URL hash (shareable, cross-device, survives refresh)
//   2. localStorage quizState (only present on the device that took the quiz)
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { decodeUrlState } from '@/lib/url-state';
import { loadQuizState } from '@/features/quiz/persistence';
import { decodeAnswersFromUrl } from '@/features/quiz/url-codec';
import { buildMapBridgeTeaser } from '@/features/quiz/answers-to-filters';
import { useRegionsIt } from '@/features/recommender/data';
import { useFocusedIstats, useFocusedRegion } from '@/features/places/useFocusedIstats';
import { useFiltersStore } from './store';
import { uiLang } from '@/i18n/lang';

const SESSION_KEY = 'rm:quiz-profile-banner:v1';

interface PillState {
  visible: boolean;
  teaser: string | null;
}

export function QuizProfileBanner() {
  const focused = useFocusedIstats();
  const regionCode = useFocusedRegion();
  const hasFocus = focused.size > 0;

  // Focus mode short-circuits the legacy pill — the user has explicit
  // recommendations to look at, the persona teaser is redundant noise.
  if (hasFocus) {
    return <FocusBanner istatCount={focused.size} regionCode={regionCode} />;
  }
  return <QuizPill />;
}

// ─── FOCUS MODE ──────────────────────────────────────────────────────────────

function FocusBanner({
  istatCount,
  regionCode,
}: {
  istatCount: number;
  regionCode: string | null;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [search] = useSearchParams();
  const { data: regionsFile } = useRegionsIt();

  const regionName = useMemo(() => {
    if (!regionCode || !regionsFile) return null;
    const numCode = Number(regionCode);
    const r = regionsFile.regions.find((reg) => reg.reg_code === numCode);
    return r?.name ?? null;
  }, [regionCode, regionsFile]);

  const handleClear = () => {
    const next = new URLSearchParams(search);
    next.delete('focus');
    next.delete('region');
    useFiltersStore.getState().reset();
    const qs = next.toString();
    const to = qs
      ? `${location.pathname}?${qs}${location.hash}`
      : `${location.pathname}${location.hash}`;
    navigate(to, { replace: true });
  };

  const lang = uiLang(i18n.language);
  const isEn = lang === 'en';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="map-focus-banner"
      className="absolute left-0 right-0 top-0 z-[1100] flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--color-terra-700)] bg-[color:var(--color-terra-600)] px-4 py-2 text-[color:var(--color-paper)] shadow-md"
    >
      <div className="flex min-w-0 items-center gap-2">
        <span aria-hidden="true" className="text-base leading-none">★</span>
        <span className="truncate font-mono text-xs tracking-wider">
          {t('map.focusBanner.label', {
            count: istatCount,
            region: regionName ?? t('map.focusBanner.regionFallback', {
              defaultValue: isEn ? 'your region' : 'вашему региону',
            }),
            defaultValue: isEn
              ? '{{count}} recommended comuni · {{region}}'
              : '{{count}} рекомендованных комуни · {{region}}',
          })}
        </span>
      </div>
      <button
        type="button"
        onClick={handleClear}
        className="shrink-0 rounded-full border border-[color:var(--color-paper)] bg-transparent px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-paper)]"
      >
        {t('map.focusBanner.clear', { defaultValue: isEn ? 'Clear' : 'Сбросить' })}
      </button>
    </div>
  );
}

// ─── QUIZ MODE (legacy pill) ─────────────────────────────────────────────────

function QuizPill() {
  const { t, i18n } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  const [pill] = useState<PillState>(() => {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY)) {
      return { visible: false, teaser: null };
    }
    const url = decodeUrlState(window.location.hash);
    if (!url.fromQuiz) return { visible: false, teaser: null };

    sessionStorage?.setItem(SESSION_KEY, '1');

    // Prefer URL-blob answers (works for shared links) and fall back to
    // localStorage (works for the device that took the quiz, no `q=` in URL).
    const urlAnswers = url.quizResultBlob ? decodeAnswersFromUrl(url.quizResultBlob) : null;
    const answers = urlAnswers ?? loadQuizState()?.answers ?? null;
    const lang = (uiLang(document.documentElement.lang)) as 'en' | 'ru';
    const teaser = answers ? buildMapBridgeTeaser(answers, lang) : null;
    return { visible: true, teaser };
  });

  const handleReset = () => {
    useFiltersStore.getState().reset();
    setDismissed(true);
  };

  if (!pill.visible || dismissed) return null;

  const lang = uiLang(i18n.language);
  const isEn = lang === 'en';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 top-4 z-[1100] -translate-x-1/2 flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-full border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] px-4 py-2.5 shadow-lg"
    >
      <span aria-hidden="true" className="shrink-0 text-[10px] text-[color:var(--color-terra-600)]">
        ✓
      </span>
      <span className="font-mono text-[11px] text-[color:var(--color-ink-700)]">
        {t('map.quizBanner.label', {
          defaultValue: isEn ? 'Quiz filters active' : 'Фильтры из квиза активны',
        })}
        {pill.teaser && (
          <span className="ml-1.5 text-[color:var(--color-ink-400)]">· {pill.teaser}</span>
        )}
      </span>
      <button
        type="button"
        onClick={handleReset}
        className="shrink-0 font-mono text-[11px] text-[color:var(--color-ink-500)] hover:text-[color:var(--color-ink-900)]"
      >
        {t('map.quizBanner.reset', { defaultValue: isEn ? 'Reset' : 'Сбросить' })}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('map.quizBanner.dismiss', { defaultValue: isEn ? 'Dismiss' : 'Закрыть' })}
        className="shrink-0 text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
      >
        ✕
      </button>
    </div>
  );
}
